import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Fuel, Flame, Droplets } from 'lucide-react';
import { AnimateOnScroll } from './AnimateOnScroll';
export function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    // Trigger entrance animations after mount
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative w-full overflow-hidden pt-16 pb-28 sm:pt-20 sm:pb-32 lg:pt-32 lg:pb-48 transition-colors duration-300" style={{
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)'
  }} data-id="element-123">
      {/* Background Geometric Accents */}
      <div className="absolute top-0 right-0 w-1/3 h-full -skew-x-12 transform origin-top translate-x-1/4 z-0 transition-colors duration-300" style={{
      backgroundColor: 'var(--bg-tertiary)'
    }} data-id="element-124" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FCEE02]/5 rounded-full blur-3xl z-0" data-id="element-125" />

      {/* Diagonal Strips */}
      <div className="absolute top-20 right-20 w-32 h-2 bg-[#FCEE02] rotate-45 hidden md:block" data-id="element-126" />
      <div className="absolute bottom-20 left-10 w-2 h-32 bg-[#EC2424] -rotate-12 hidden md:block" data-id="element-127" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 md:px-12" data-id="element-128">
        <div className="max-w-4xl" data-id="element-129">
          {/* Tagline */}
          <div className="flex items-center gap-3 sm:gap-4 mb-6" style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s'
        }} data-id="element-130">
            <div className="h-1 w-8 sm:w-12 bg-[#FCEE02]" data-id="element-131" />
            <span className="font-bold tracking-widest uppercase text-xs sm:text-sm" style={{
            color: 'var(--accent-yellow-text)'
          }} data-id="element-132">
              Distribuidora de Combustibles
            </span>
          </div>

          {/* Title */}
          <h1 className="text-5xl sm:text-6xl md:text-8xl font-black uppercase tracking-tighter leading-[0.9] mb-6 sm:mb-8 transition-colors duration-300" style={{
          color: 'var(--text-primary)',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.35s'
        }} data-id="element-133">
            Junquito <span className="text-[#FCEE02]" data-id="element-134">Gas</span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl md:text-2xl max-w-2xl mb-8 sm:mb-10 font-medium leading-relaxed border-l-4 border-[#FCEE02] pl-4 sm:pl-6 transition-colors duration-300" style={{
          color: 'var(--text-secondary)',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.5s'
        }} data-id="element-135">
            Ofrecemos{' '}
            <strong style={{
            color: 'var(--text-primary)'
          }} data-id="element-136">
              GLP, Gasoil, Gasolina
            </strong>{' '}
            y más. Nuestra gasolina es distribuida en colaboración con{' '}
            <strong style={{
            color: 'var(--text-primary)'
          }} data-id="element-137">
              NEXT
            </strong>
            . Combustible confiable y seguro para sus vehículos, hogares y
            negocios en República Dominicana.
          </p>

          {/* Product Tags */}
          <div className="flex flex-wrap gap-3 mb-10 sm:mb-12" style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.65s'
        }} data-id="element-138">
            {[{
            label: 'GLP',
            icon: Flame,
            color: '#FCEE02'
          }, {
            label: 'Gasolina',
            icon: Fuel,
            color: '#FB900D'
          }, {
            label: 'Gasoil',
            icon: Droplets,
            color: '#EC2424'
          }].map(product => <span key={product.label} className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider border rounded-full" style={{
            borderColor: product.color,
            color: product.color,
            backgroundColor: `${product.color}10`
          }} data-id="element-139">
                <product.icon className="w-4 h-4" data-id="element-140" />
                {product.label}
              </span>)}
            <span className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-bold uppercase tracking-wider border rounded-full" style={{
            borderColor: 'var(--border-color-strong)',
            color: 'var(--text-secondary)'
          }} data-id="element-141">
              <Fuel className="w-4 h-4" data-id="element-142" />
              Powered by NEXT
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6" style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.8s'
        }} data-id="element-143">
            <button onClick={() => {
            navigate('/solicitar');
            window.scrollTo(0, 0);
          }} className="group relative px-6 sm:px-8 py-4 bg-[#FCEE02] text-[#251F1F] font-black uppercase tracking-wider hover:bg-white hover:text-[#251F1F] transition-colors duration-200 border-2 border-[#FCEE02] text-sm sm:text-base rounded-xl" data-id="element-144">
              <span className="flex items-center justify-center gap-2" data-id="element-145">
                Solicitar Servicio
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-146" />
              </span>
            </button>

            <button onClick={() => scrollToSection('estaciones')} className="px-6 sm:px-8 py-4 border-2 font-bold uppercase tracking-wider transition-colors duration-200 text-sm sm:text-base rounded-xl" style={{
            borderColor: 'var(--text-primary)',
            color: 'var(--text-primary)'
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'var(--text-primary)';
            e.currentTarget.style.color = 'var(--bg-primary)';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--text-primary)';
          }} data-id="element-147">
              Ver Estaciones
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Diagonal Cut */}
      <div className="absolute bottom-0 left-0 w-full h-12 sm:h-16 z-20 transition-colors duration-300" style={{
      backgroundColor: 'var(--bg-secondary)',
      clipPath: 'polygon(0 100%, 100% 100%, 100% 0)'
    }} data-id="element-148" />
    </section>;
}